import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import React, { useContext, useEffect } from 'react';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Inline, xcss, Stack, Text, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import SectionMessage from '@atlaskit/section-message';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';

import { CoreInvitesContext, CoreInvitesSource } from '@confluence/core-invites-provider';
import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller';
import { PressableLink } from '../../../PressableLink';
import type { UserPickerEmailPrincipal } from '../../commonTypes';

const i18n = defineMessages({
	inviteMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.invite.text',
		defaultMessage:
			'Invite <b>{userEmail} {userCount, plural, =0 {} one {and # other} other {and # others}}</b> to join Confluence? Otherwise, we’ll just send a notification when you select Share.',
		description: 'Tells the user there might be restrictions on this content before sharing',
	},
	warningInviteMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.warning.invite.text',
		defaultMessage:
			'Sharing to emails isn’t available on the Free plan. You can invite emails to join Confluence, or remove them to continue sharing.',
		description:
			'Warning message shown when a user tries to share with email addresses while on the Free or trial plan, informing them of their options.',
	},
	invitePeopleCta: {
		id: 'share-and-restrict-dialog.button.restriction.invite.action.text',
		defaultMessage: 'Invite them',
		description: 'Action for user to change the restrictions on this content being shared',
	},
	inviteToConfluenceCta: {
		id: 'share-and-restrict-dialog.button.restriction.inviteToConfluence.action.text',
		defaultMessage: 'Invite to Confluence',
		description: 'Action for user to change the restrictions on this content being shared',
	},
	notifyUser: {
		id: 'share-and-restrict-dialog.button.restriction.notify.action.text',
		defaultMessage: 'Notify only',
		description: 'Action for the user to leave the restrictions of this content as is.',
	},
	inviteAndNotifyMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.inviteOrNotify.text',
		defaultMessage:
			'To view the shared page, <b>{userEmail}</b> <b> {userCount, plural, =0 {} one {and # other} other {and # others}}</b> might need Confluence access. Invite them to get started. If sharing with a group or list, you can choose to notify only.',
		description: 'Informs the user there might be restrictions on this content before sharing',
	},
});

type InviteMessageProps = {
	userEmails: UserPickerEmailPrincipal[] | undefined | null;
	hiddenEmails?: string[];
	setHiddenEmails?: (emails: string[]) => void;
	showEmailWarningMessage: boolean;
};

const inviteMessageContainer = xcss({
	padding: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const warningInviteMessageContainer = xcss({
	backgroundColor: 'color.background.warning',
	marginTop: 'space.200',
	marginLeft: 'space.200',
	marginRight: 'space.200',
});

const iconContainer = xcss({
	marginTop: 'space.050',
});

const Bold = (chunks: any) => <Text weight="bold">{chunks}</Text>;

export const InviteMessageComponent = ({
	userEmails,
	hiddenEmails,
	setHiddenEmails,
	showEmailWarningMessage,
}: InviteMessageProps) => {
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { locale } = useIntl();
	const isEnglishLocale = locale.toLowerCase().startsWith('en');

	useEffect(() => {
		if (!openCoreInvites || !userEmails || userEmails?.length === 0) return;

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'informInviteOnShare',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					inviteUserImprovementM1Cohort: isEnglishLocale
						? expVal('invite-usd-improvements-m1', 'cohort', 'not-enrolled')
						: 'not-enrolled',
					isInviteUserImprovementM1CohortEnabled:
						isEnglishLocale && expValEquals('invite-usd-improvements-m1', 'cohort', 'variation'),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, openCoreInvites, userEmails, isEnglishLocale]);

	if (!openCoreInvites || !userEmails || userEmails?.length === 0) return null;

	const handleCtaClick = () => {
		const isInviteUserImprovementM1CohortEnabled =
			isEnglishLocale && expValEquals('invite-usd-improvements-m1', 'cohort', 'variation');
		if (isInviteUserImprovementM1CohortEnabled) {
			openCoreInvites(
				CoreInvitesSource.SHARE_AND_RESTRICT_DIALOG,
				{
					onInviteCompleted: () => setHiddenEmails?.(userEmails.map((email) => email.id)),
				},
				userEmails
					.filter((user) => !hiddenEmails?.includes(user.id))
					.map(({ id, name, type }) => ({
						email: id,
						name,
						type,
						id,
					})),
			);
		} else {
			openCoreInvites(
				CoreInvitesSource.SHARE_AND_RESTRICT_DIALOG,
				undefined,
				userEmails.map(({ id, name, type }) => ({
					email: id,
					name,
					type,
					id,
				})),
			);
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'invitePeople',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					inviteUserImprovementM1Cohort: isEnglishLocale
						? expVal('invite-usd-improvements-m1', 'cohort', 'not-enrolled')
						: 'not-enrolled',
					isInviteUserImprovementM1CohortEnabled,
					emailCount: isInviteUserImprovementM1CohortEnabled
						? userEmails.filter((user) => !hiddenEmails?.includes(user.id)).length
						: userEmails?.length,
				},
			},
		}).fire();
	};

	const handleNotifyUserCtaClick = () => {
		setHiddenEmails?.(userEmails.map((email) => email.id));
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'notifyOnly',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					emailCount: userEmails?.length,
				},
			},
		}).fire();
	};

	const allEmailsHidden = userEmails.every((user) => hiddenEmails?.includes(user.id));
	const emailDiff = userEmails.filter((user) => !hiddenEmails?.includes(user.id));

	if (allEmailsHidden && expValEquals('invite-usd-improvements-m1', 'cohort', 'variation')) {
		return null;
	}

	return isEnglishLocale && expValEquals('invite-usd-improvements-m1', 'cohort', 'variation') ? (
		<SectionMessage
			appearance="warning"
			actions={[
				<Text color="color.link" weight="semibold" size="medium" key="invitePeople">
					<PressableLink isBlue onClick={handleCtaClick}>
						<FormattedMessage {...i18n.inviteToConfluenceCta} />
					</PressableLink>
				</Text>,
				<Text color="color.link" weight="semibold" size="medium" key="notifyOnly">
					<PressableLink isBlue onClick={handleNotifyUserCtaClick}>
						<FormattedMessage {...i18n.notifyUser} />
					</PressableLink>
				</Text>,
			]}
		>
			<Stack alignInline="start" space="space.100">
				<Text align="start" color="color.text.subtle" size="medium">
					<FormattedMessage
						{...i18n.inviteAndNotifyMessage}
						values={{
							userEmail: emailDiff[0]?.id,
							userCount: emailDiff?.length - 1,
							b: Bold,
						}}
					/>
				</Text>
			</Stack>
		</SectionMessage>
	) : (
		<Inline
			alignBlock="start"
			space="space.200"
			xcss={[inviteMessageContainer, showEmailWarningMessage && warningInviteMessageContainer]}
		>
			<Box xcss={iconContainer}>
				{showEmailWarningMessage ? (
					<StatusWarningIcon label="" color={token('color.icon.warning')} />
				) : (
					<StatusInformationIcon label="" color={token('color.icon.subtle')} />
				)}
			</Box>
			<Stack alignInline="start" space="space.100">
				<Text align="start" color="color.text.subtle" size="medium">
					{showEmailWarningMessage ? (
						<FormattedMessage {...i18n.warningInviteMessage} />
					) : (
						<FormattedMessage
							{...i18n.inviteMessage}
							values={{
								userEmail: userEmails[0]?.id,
								userCount: userEmails?.length - 1,
								b: Bold,
							}}
						/>
					)}
				</Text>
				<Text color="color.link" weight="semibold" size="medium">
					<PressableLink isBlue onClick={handleCtaClick}>
						<FormattedMessage {...i18n.invitePeopleCta} />
					</PressableLink>
				</Text>
			</Stack>
		</Inline>
	);
};

export const InviteMessage = (props: InviteMessageProps) => (
	<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ADOPTION}>
		<InviteMessageComponent {...props} />
	</TransparentErrorBoundary>
);
